import React, { useEffect, useState } from "react";
import ErrorDisplay from "../../common/ErrorDisplay";
import { Problem } from "../../api/backendApi";
import {
  ComponentFlagModel,
  modelInfoApi,
  VehicleComponentsModel,
} from "../../api/modelInfoApi";
import {
  Alert,
  Badge,
  Button,
  ButtonToolbar,
  Col,
  Modal,
  Row,
} from "react-bootstrap";
import RescueSheetViewer from "../../components/RescueSheetViewer";
import "./ComponentEditor.css";
import ContextMenu, {
  ContextMenuSeparatorText,
  ContextMenuOption,
} from "../../components/contextmenus/ContextMenu";
import { ArrowClockwise } from "react-bootstrap-icons";
import {
  drawingApi,
  DrawingFileTypeModel,
  LocaleModel,
} from "../../api/drawingApi";

interface ComponentDictionary<V> {
  [key: string]: V;
}

interface ComponentCell {
  flag: ComponentFlagModel;
  originalValue: ComponentFlagModel;
  selected: boolean;
}

class VehicleComponentsRow {
  constructor(
    public recId: number,
    public description: string,
    public components: ComponentDictionary<ComponentCell>,
    public isNew: boolean,
  ) {}

  public toModel(components: string[]): VehicleComponentsModel {
    const newComponents: ComponentDictionary<ComponentFlagModel> = {};

    components.forEach((key) => {
      newComponents[key] = this.components[key].flag;
    });

    return {
      recId: this.recId,
      description: this.description,
      components: newComponents,
      isNew: false,
    };
  }

  public copyVehicleComponentsRow(components: string[]): VehicleComponentsRow {
    const newComponents: ComponentDictionary<ComponentCell> = {};

    components.forEach((key) => {
      newComponents[key] = { ...this.components[key] };
    });

    return new VehicleComponentsRow(
      this.recId,
      this.description,
      {
        ...this.components,
      },
      this.isNew,
    );
  }

  public getCopyWithoutModificationAndNewFlag(
    components: string[],
  ): VehicleComponentsRow {
    const newComponents: ComponentDictionary<ComponentCell> = {};

    components.forEach((key) => {
      newComponents[key] = { ...this.components[key] };
      newComponents[key].originalValue = newComponents[key].flag;
    });

    return new VehicleComponentsRow(
      this.recId,
      this.description,
      {
        ...newComponents,
      },
      false,
    );
  }

  public static fromModel(
    components: string[],
    model: VehicleComponentsModel,
  ): VehicleComponentsRow {
    const newComponents: ComponentDictionary<ComponentCell> = {};

    components.forEach((key) => {
      newComponents[key] = {
        flag: model.components[key] ?? ComponentFlagModel.NotAvailable,
        originalValue: model.components[key] ?? ComponentFlagModel.NotAvailable,
        selected: false,
      };
    });

    return new VehicleComponentsRow(
      model.recId,
      model.description,
      newComponents,
      model.isNew,
    );
  }

  hasChanges(components: string[]): boolean {
    return (
      components.findIndex(
        (c) => this.components[c].flag !== this.components[c].originalValue,
      ) >= 0
    );
  }
}

interface ComponentEditorProps {
  vehicleFileNumber: string;
  isModelInfoModified: boolean;
  readOnly: boolean;
  fileVersion: number | undefined;
  hasMpfzFile: boolean;
  onModified: (modified: boolean) => void;
}

function ComponentEditor({
  vehicleFileNumber,
  isModelInfoModified,
  readOnly,
  fileVersion,
  hasMpfzFile,
  onModified,
}: ComponentEditorProps) {
  const [error, setError] = useState<Problem>();
  const [components, setComponents] = useState<string[]>([]);
  const [oldComponents, setOldComponents] = useState<string[]>();
  const [newComponents, setNewComponents] = useState<string[]>();
  const [vehicles, setVehicles] = useState<VehicleComponentsRow[]>();
  const [viewingRecId, setViewingRecId] = useState<number>();
  const [viewingComponentString, setViewingComponentString] =
    useState<string>();
  const [viewingForceRefresh, setViewingForceRefresh] = useState(1);

  const [contextMenuOptions, setContextMenuOptions] = useState<
    ContextMenuOption[]
  >([]);
  const [contextMenuPosition, setContextMenuPosition] = useState({
    x: 0,
    y: 0,
  });
  const [firstSelectedVehicle, setFirstSelectedVehicle] =
    useState<VehicleComponentsRow>();
  const [firstSelectedComponent, setFirstSelectedComponent] =
    useState<string>();

  const [rescueSheetModalVisible, setRescueSheetModalVisible] = useState(false);
  const [newDrawingWarningVisible, setNewDrawingWarningVisible] =
    useState(false);

  const [currentDisplayerFileVersion, setCurrentDisplayerFileVersion] =
    useState(fileVersion ?? 0);

  const loadVehicleList = async () => {
    const result =
      await modelInfoApi.getVehiclesWithComponents(vehicleFileNumber);

    if (result.ok) {
      setError(undefined);
      setComponents(result.value.filteredComponents);
      setOldComponents(result.value.oldComponents);
      setNewComponents(result.value.newComponents);
      var vehicles = result.value.vehicles.map((v) =>
        VehicleComponentsRow.fromModel(result.value.filteredComponents, v),
      );
      setVehicles(vehicles);
      setNewDrawingWarningVisible(false);
      setCurrentDisplayerFileVersion(fileVersion ?? 0);
      onModified(
        anyChanges(vehicles, true) ||
          (result.value.newComponents && result.value.newComponents.length > 0),
      );
    } else {
      setError(result.problem);
    }
  };

  useEffect(() => {
    loadVehicleList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleFileNumber, isModelInfoModified]);

  useEffect(() => {
    // new file version available?
    if (fileVersion && fileVersion > currentDisplayerFileVersion) {
      setNewDrawingWarningVisible(true);
      onModified(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileVersion]);

  const handleSave = async () => {
    const result = await modelInfoApi.updateVehicleComponents(
      vehicleFileNumber,
      (vehicles ?? []).map((v) => v.toModel(components)),
    );

    if (result.ok) {
      setError(undefined);
      setOldComponents(undefined);
      setNewComponents(undefined);
      setVehicles(
        vehicles?.map((v) =>
          v.getCopyWithoutModificationAndNewFlag(components),
        ),
      );
      onModified(false);
    } else {
      setError(result.problem);
    }
  };

  const handleViewSvg = (vehicle: VehicleComponentsRow) => {
    setViewingRecId(vehicle.recId);
    setViewingComponentString(
      modelInfoApi.convertToComponentString(vehicle.toModel(components)),
    );
    setViewingForceRefresh(viewingForceRefresh + 1);
  };

  const selectCell = (
    vehicle: VehicleComponentsRow,
    component: string,
    ctrlKey: boolean,
    shiftKey: boolean,
  ) => {
    if (readOnly) return;

    const alreadySelected = vehicle.components[component].selected;

    if (!alreadySelected && ctrlKey) {
      // add to selection
      const newVehicles = vehicles?.map((v) => {
        if (v !== vehicle) return v;

        let copy = v.copyVehicleComponentsRow(components);

        copy.components[component].selected = true;

        return copy;
      });
      setVehicles(newVehicles);
      if (!firstSelectedVehicle && !firstSelectedComponent) {
        setFirstSelectedComponent(component);
        setFirstSelectedVehicle(vehicle);
      }
    } else if (alreadySelected && ctrlKey) {
      // do nothing
    } else if (!ctrlKey && !shiftKey) {
      // overwrite selection
      setVehicles(
        vehicles?.map((v) => {
          if (v !== vehicle) {
            const anySelected = components
              .map((c) => v.components[c].selected)
              .filter((c) => c === true);

            if (anySelected.length === 0) return v;

            let copy = v.copyVehicleComponentsRow(components);
            components.forEach((c) => {
              copy.components[c].selected = false;
            });

            return copy;
          } else {
            let copy = v.copyVehicleComponentsRow(components);
            components.forEach((c) => {
              copy.components[c].selected = c !== component ? false : true;
              if (copy.components[c].selected) {
                setFirstSelectedComponent(component);
                setFirstSelectedVehicle(vehicle);
              }
            });

            return copy;
          }
        }),
      );
    }
  };

  const selectCells = (
    vehicle: VehicleComponentsRow,
    component: string,
    shiftKey: boolean,
  ) => {
    if (readOnly) return;

    if (shiftKey && firstSelectedVehicle && firstSelectedComponent) {
      let newVehicles = vehicles?.map((v) => {
        let copy = v.copyVehicleComponentsRow(components);
        components.forEach((c) => {
          copy.components[c].selected = false;
        });

        if (v.recId === firstSelectedVehicle?.recId) {
          copy.components[firstSelectedComponent].selected = true;
        }
        if (v === vehicle) {
          copy.components[component].selected = true;
        }

        return copy;
      });

      let selectedVehicles = newVehicles?.filter((v) => {
        return components.some((c) => v.components[c].selected);
      });

      if (newVehicles !== undefined && selectedVehicles !== undefined) {
        const indexesVehicles = selectedVehicles
          .map((sv) =>
            newVehicles
              ? newVehicles.findIndex((v) => v.recId === sv.recId)
              : -1,
          )
          .sort((a, b) => a - b);
        const indexesComponents = components
          .map((c, index) =>
            selectedVehicles
              ? selectedVehicles.some((v) => v.components[c].selected)
                ? index
                : -1
              : -1,
          )
          .filter((index) => index !== -1)
          .sort((a, b) => a - b);

        for (
          let i = indexesVehicles[0];
          i <= indexesVehicles[indexesVehicles.length - 1];
          i++
        ) {
          for (
            let x = indexesComponents[0];
            x <= indexesComponents[indexesComponents.length - 1];
            x++
          ) {
            newVehicles[i].components[components[x]].selected = true;
          }
        }

        setVehicles(newVehicles);
      }
    }
  };

  const handleContextMenu = (
    event: React.MouseEvent<HTMLTableCellElement>,
    vehicle: VehicleComponentsRow,
    component: string | undefined,
  ) => {
    const target = event.target as typeof event.target & Element;

    // don't trigger on inputs (they have their own context menu)
    if (target.nodeName === "INPUT") return;

    event.preventDefault();

    const position = {
      x: event.clientX,
      y: event.clientY,
    };

    if (component && !readOnly) {
      if (!vehicle.components[component].selected) {
        selectCell(vehicle, component, false, false);
      }
    }

    setContextMenuPosition(position);

    const options =
      !component || readOnly
        ? [
            {
              text: "View SVG",
              data: () => handleViewSvg(vehicle),
            },
          ]
        : [
            {
              text: "Set all to Standard",
              data: () => handleContextMenuOption(ComponentFlagModel.Standard),
            },
            {
              text: "Set all to Optional",
              data: () => handleContextMenuOption(ComponentFlagModel.Optional),
            },
            {
              text: "Set all to Not Available",
              data: () =>
                handleContextMenuOption(ComponentFlagModel.NotAvailable),
            },
            { text: ContextMenuSeparatorText },
            { text: "View SVG", data: () => handleViewSvg(vehicle) },
          ];

    setContextMenuOptions(options);
  };

  const handleContextMenuOption = (flag: ComponentFlagModel) => {
    const newVehicles =
      vehicles?.map((v) => {
        const anySelected = components
          .map((c) => v.components[c].selected)
          .filter((c) => c === true);

        if (anySelected.length === 0) return v;

        let copy = v.copyVehicleComponentsRow(components);
        components.forEach((c) => {
          if (copy.components[c].selected) {
            copy.components[c].flag = flag;
          }
        });

        return copy;
      }) ?? [];

    setVehicles(newVehicles);
    onModified(anyChanges(newVehicles, false));
  };

  const anyChanges = (
    vehicles: VehicleComponentsRow[] | undefined,
    includeNew: boolean,
    includeNewComponents: boolean = true,
  ): boolean => {
    if (!vehicles) return false;

    for (let ii = 0; ii < vehicles.length; ++ii) {
      if (
        (includeNew && vehicles[ii].isNew) ||
        vehicles[ii].hasChanges(components) ||
        (includeNewComponents && newComponents && newComponents.length > 0)
      ) {
        return true;
      }
    }

    return false;
  };

  const handleReload = () => {
    if (anyChanges(vehicles, false, false)) {
      if (
        !window.confirm("There are modifications to the data. Are you sure?")
      ) {
        return;
      }
    }

    loadVehicleList();
    onModified(false);
  };

  const showPdf = async (pdfId: string, locale: LocaleModel) => {
    const response = await drawingApi.downloadDrawingFile(
      vehicleFileNumber,
      pdfId + ".pdf",
      DrawingFileTypeModel.PDF,
      locale,
      true,
    );

    if (response.ok) {
      setError(undefined);
    } else {
      setError(response.problem);
    }
  };

  const colSpan = 2;

  return (
    <div
      className="h-100"
      style={{ display: "flex", flexDirection: "column", rowGap: "1rem" }}
    >
      <div>
        <ErrorDisplay error={error}></ErrorDisplay>

        {!hasMpfzFile && (
          <Alert variant="warning">
            An .mpfz for this vehicle file is not available.
          </Alert>
        )}

        {oldComponents && oldComponents.length > 0 && (
          <Alert variant="warning">
            The following components do not exist in the drawing and will be
            removed: {oldComponents.join(", ")}
          </Alert>
        )}
        {hasMpfzFile && (
          <Row className="g-0">
            <Col style={{ maxWidth: "fit-content" }}>
              <ButtonToolbar>
                {!readOnly && (
                  <Button
                    size="sm"
                    onClick={() => handleSave()}
                    disabled={!anyChanges(vehicles, true)}
                    className="me-2"
                  >
                    Save Components
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="success"
                  onClick={() => handleReload()}
                >
                  <ArrowClockwise />
                  &nbsp;Reload
                </Button>
              </ButtonToolbar>
            </Col>
            {newDrawingWarningVisible && (
              <Col className="ms-5">
                <Badge bg="warning" text="dark" className="my-1">
                  New .mpfz file is available. Reload as soon as possible.
                </Badge>
              </Col>
            )}
          </Row>
        )}
      </div>
      {hasMpfzFile && (
        <>
          <div className="component-table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Rec Id</th>
                  <th>Description</th>
                  {components.map((c) => (
                    <th key={c}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {!vehicles && !error && (
                  <tr>
                    <td colSpan={colSpan + components.length}>Loading...</td>
                  </tr>
                )}
                {!vehicles && error && (
                  <tr>
                    <td colSpan={colSpan + components.length}>Error...</td>
                  </tr>
                )}
                {vehicles && vehicles.length === 0 && (
                  <tr>
                    <td
                      colSpan={colSpan + components.length}
                      style={{ textAlign: "left" }}
                    >
                      No vehicles in model info...
                    </td>
                  </tr>
                )}
                {vehicles &&
                  vehicles.length > 0 &&
                  vehicles.map((v) => (
                    <tr key={v.recId}>
                      <td
                        className="no-select"
                        onContextMenu={(e) =>
                          handleContextMenu(e, v, undefined)
                        }
                      >
                        {v.recId}
                      </td>
                      <td
                        className="no-select"
                        onContextMenu={(e) =>
                          handleContextMenu(e, v, undefined)
                        }
                      >
                        {v.description}
                      </td>
                      {components.map((c) => (
                        <td
                          key={c}
                          style={{
                            backgroundColor: v.components[c].selected
                              ? "#CCF0FF"
                              : v.isNew || newComponents?.includes(c)
                                ? "#ffe8e8"
                                : undefined,
                          }}
                          onContextMenu={(e) => handleContextMenu(e, v, c)}
                          onClickCapture={(e) => {
                            selectCells(v, c, e.shiftKey);
                            selectCell(v, c, e.ctrlKey || e.altKey, e.shiftKey);
                          }}
                        >
                          <div
                            className="btn btn-sm btn-outline-secondary"
                            style={{
                              width: "80px",
                              fontSize: "85%",
                              pointerEvents: "none",
                            }}
                          >
                            {ComponentFlagModel[v.components[c].flag]}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <div
            style={{
              minHeight: "40%",
              flex: 1,
              borderTop: "1px solid rgba(0,0,0,0.25)",
              paddingTop: "1rem",
            }}
          >
            {!rescueSheetModalVisible && (
              <>
                <RescueSheetViewer
                  vehicleFileNumber={vehicleFileNumber}
                  recId={viewingRecId}
                  componentString={viewingComponentString}
                  refreshSwitch={viewingForceRefresh}
                  onPopupClick={() => setRescueSheetModalVisible(true)}
                  useDrawing={false}
                  onPdfClick={(pdfId, locale) => showPdf(pdfId, locale)}
                />
              </>
            )}
          </div>
        </>
      )}
      {contextMenuOptions && contextMenuOptions.length > 0 && (
        <ContextMenu
          options={contextMenuOptions}
          positionX={contextMenuPosition.x}
          positionY={contextMenuPosition.y}
          onClick={(e) => {
            typeof (e.data === "function") && e.data();
            setContextMenuOptions([]);
          }}
          onClose={() => setContextMenuOptions([])}
        />
      )}
      {rescueSheetModalVisible && (
        <Modal
          show={true}
          size="xl"
          onHide={() => setRescueSheetModalVisible(false)}
        >
          <Modal.Header closeButton>
            <Modal.Title>Rescue sheet viewer</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ height: "650px" }}>
            <RescueSheetViewer
              vehicleFileNumber={vehicleFileNumber}
              recId={viewingRecId}
              componentString={viewingComponentString}
              refreshSwitch={viewingForceRefresh}
              useDrawing={false}
            />
          </Modal.Body>
        </Modal>
      )}
    </div>
  );
}

export default ComponentEditor;
